import { UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import type { CreatableMultiSelectOption } from '../../shared/components/creatable-multi-select.tsx';
import { CreatableMultiSelect } from '../../shared/components/creatable-multi-select.tsx';
import type { EventFormData } from './event-form-fields.tsx';

type EventAttendeesFieldProps = {
    control: UseFormReturn<EventFormData>['control'];
    setValue: UseFormReturn<EventFormData>['setValue'];
    customerId?: string;
    disabled?: boolean;
};

export function EventAttendeesField({
    control,
    setValue,
    customerId,
    disabled,
}: EventAttendeesFieldProps): React.JSX.Element {
    const { t } = useLocale('events');
    const [emailError, setEmailError] = useState('');
    const watchedAttendees = useWatch({ control, name: 'attendees' });
    const attendees = useMemo(() => watchedAttendees ?? [], [watchedAttendees]);

    const { data: platoUsers } = api.platoUser.query.users.getManyUsers.getList.useQuery();
    const { data: customerContacts } = api.platoUser.getManyCustomerContact.get.useQuery(
        { customerId: customerId ?? '' },
        { enabled: !!customerId },
    );

    const options: CreatableMultiSelectOption[] = useMemo(() => {
        const seen = new Set<string>();
        const result: CreatableMultiSelectOption[] = [];

        // Customer contacts first — they are the most relevant picks for a visit.
        for (const contact of customerContacts ?? []) {
            const name = getFullName(contact.firstName, contact.lastName) || contact.name || contact.email;
            if (!name) {
                continue;
            }
            // Only offer emails that pass the same z.email() check the server
            // runs on submit — a selectable suggestion must never be rejected.
            const email = contact.email?.trim().toLowerCase();
            const isSelectable = !!email && z.email().safeParse(email).success;
            if (isSelectable) {
                if (seen.has(email)) {
                    continue;
                }
                seen.add(email);
            }
            result.push({
                value: isSelectable ? email : contact.contactId,
                label: name,
                subtitle: [contact.role, contact.email?.trim() || t('events__form_attendees_no_email')]
                    .filter(Boolean)
                    .join(' · '),
                disabled: !isSelectable,
            });
        }

        for (const user of platoUsers ?? []) {
            if (user.deletedAt) {
                continue;
            }
            const email = user.email.toLowerCase();
            if (seen.has(email)) {
                continue;
            }
            seen.add(email);
            result.push({
                value: email,
                label: getFullName(user.firstName, user.lastName),
                subtitle: user.email,
            });
        }

        return result;
    }, [customerContacts, platoUsers, t]);

    return (
        <DialogForm.Item className="items-start gap-1">
            <DialogForm.Label icon={UsersIcon} />
            <DialogForm.Body className="min-w-0 flex-col gap-2">
                <CreatableMultiSelect
                    options={options}
                    value={attendees}
                    onChange={(emails) => {
                        setValue('attendees', emails);
                        setEmailError('');
                    }}
                    placeholder={t('events__form_attendees_placeholder')}
                    disabled={disabled}
                    variant="inline"
                    isValidNewOption={(input) => input.trim().includes('@')}
                    onCreateOption={(input) => input.trim().toLowerCase()}
                    onInvalidOption={() => {
                        setEmailError(t('events__form_attendees_invalid_email'));
                    }}
                    formatOptionLabel={(option) => (
                        <div className="flex flex-col">
                            <Text size="sm" color="neutral-950">
                                {option.label}
                            </Text>
                            <Text size="xs" color="neutral-500">
                                {option.subtitle}
                            </Text>
                        </div>
                    )}
                    errorMessage={emailError}
                />
            </DialogForm.Body>
        </DialogForm.Item>
    );
}
